// HTML template registry — the 8 starter templates the prototype's Templates
// picker offers for `html`-kind quiz questions. Each entry exposes
// `tooltipParams` (the inline-tooltip slots the template HTML uses via
// `{{name}}` placeholders); Authors fill in the per-use, per-language
// tooltip text via `tooltipValues` on a quiz question (see
// `QuizQuestionSchema` in layouts.ts).
//
// This registry is the codebase-side mirror of the prototype's
// `HTML_TEMPLATES` constant in `src/html-templates.jsx`. It is intentionally
// minimal — only the fields the gateway validators (this PR) and the
// build-step (next PR) need. Prototype-facing metadata (name, category,
// complexity, description) lives in the prototype.
//
// **What's NOT here (deliberately, for D2-narrow scope):**
// - The actual template HTML files. Shipping those (and the legacy
//   `GL.Hover*` HTML scanner) is deferred until the production Editor
//   exposes Admin-uploaded templates.
// - Author-facing strings (template name, description) — those stay
//   prototype-side until the production Editor lands.
//
// See `wiki/Tooltip_text_editing_design.md` §5.2 and the Phase 2 prototype
// audit at hash `IDwg-p7N4-Wn3L9lk_4s3g` for the source.
import { z } from "zod";
import { TooltipParamSchema } from "./shared.js";
/**
 * An HTML template entry — the minimal shape the gateway validators need.
 *
 * `htmlUrl` is the canonical path the prototype writes into a quiz
 * question's `content.html.htmlUrl` field when the Author picks this
 * template. The validator matches a question's `htmlUrl` against this
 * field to look up the template's slot names.
 */
export const HtmlTemplateSchema = z
    .object({
    id: z.string().min(1),
    htmlUrl: z.string().min(1),
    tooltipParams: z.array(TooltipParamSchema),
})
    .strict();
const HTML_SAMPLE_ROOT = "uploads/html-samples/content/en/modules/";
/**
 * The 8 starter templates. Mirrors the prototype's `HTML_TEMPLATES`
 * registry (only the validator-relevant fields). Update this list when
 * the prototype's registry changes or when production-Editor-uploaded
 * templates are introduced.
 *
 * Slot names are referenced by `tooltipValues` keys on quiz questions;
 * the gateway's `findUnknownTooltipKeys` validator rejects exports
 * whose keys don't appear here.
 */
export const HTML_TEMPLATE_REGISTRY = [
    {
        id: "tpl-single-image",
        htmlUrl: HTML_SAMPLE_ROOT + "module_4/05.html",
        tooltipParams: [
            slot("sender_domain", "Sender domain"),
            slot("generic_greeting", "Generic greeting"),
            slot("reset_link", "Reset link"),
            slot("urgency_deadline", "Urgency deadline"),
        ],
    },
    {
        id: "tpl-annotated-screenshot",
        htmlUrl: HTML_SAMPLE_ROOT + "module_3/05A/04.html",
        tooltipParams: [
            slot("unknown_sender", "Unknown sender"),
            slot("gift_card_payment", "Gift-card payment"),
            slot("urgency_pressure", "Urgency pressure"),
            slot("secrecy_request", "Secrecy request"),
        ],
    },
    {
        id: "tpl-multi-image-mockup",
        htmlUrl: HTML_SAMPLE_ROOT + "module_4/09.html",
        tooltipParams: [
            slot("bank_detail_request", "Bank-detail request"),
            slot("unfamiliar_portal", "Unfamiliar portal"),
            slot("payday_urgency", "Payday urgency"),
        ],
    },
    {
        id: "tpl-phishy-email-typical",
        htmlUrl: HTML_SAMPLE_ROOT + "module_4/03.html",
        tooltipParams: [
            slot("sender_domain", "Sender domain"),
            slot("personal_info_request", "Personal-info request"),
            slot("suspicious_link", "Suspicious link"),
            slot("urgency_deadline", "Urgency deadline"),
        ],
    },
    {
        id: "tpl-phishy-email-heavy-tooltips",
        htmlUrl: HTML_SAMPLE_ROOT + "module_4/01.html",
        tooltipParams: [
            slot("sender_domain", "Sender domain"),
            slot("prize_bait", "Prize bait"),
            slot("upfront_fee", "Upfront fee"),
            slot("urgency_deadline", "Urgency deadline"),
        ],
    },
    {
        id: "tpl-fake-login",
        htmlUrl: HTML_SAMPLE_ROOT + "module_2/02A/01.html",
        tooltipParams: [
            slot("spoofed_domain", "Spoofed domain"),
            slot("confirm_password", "Confirm-password trap"),
        ],
    },
    {
        id: "tpl-social-oversharing",
        htmlUrl: HTML_SAMPLE_ROOT + "module_3/05A/01.html",
        tooltipParams: [
            slot("empty_home_dates", "Empty-home dates"),
            slot("home_address", "Home address"),
            slot("spare_key", "Spare-key location"),
        ],
    },
    {
        id: "tpl-smishing-text",
        htmlUrl: HTML_SAMPLE_ROOT + "module_2/02A/06.html",
        tooltipParams: [
            slot("unknown_number", "Unknown number"),
            slot("no_tracking", "No tracking detail"),
            slot("redelivery_fee", "Redelivery fee"),
            slot("shortened_link", "Shortened link"),
        ],
    },
];
// Validate the registry shape at module-load time. A registry entry with a
// malformed TooltipParam or duplicate slot names within the same template
// would silently propagate to the validator; catching it here surfaces it
// during test or build. (Throws synchronously at first import — desired.)
for (const template of HTML_TEMPLATE_REGISTRY) {
    HtmlTemplateSchema.parse(template);
    const seenNames = new Set();
    for (const param of template.tooltipParams) {
        if (seenNames.has(param.name)) {
            throw new Error(`HTML_TEMPLATE_REGISTRY: template "${template.id}" has duplicate ` +
                `tooltipParams slot name "${param.name}"`);
        }
        seenNames.add(param.name);
    }
}
/**
 * Resolve a quiz question's `content.html.htmlUrl` to the registry entry
 * that defines its tooltip slots. Returns `undefined` when no template
 * matches — the gateway treats unknown templates as not-our-concern (the
 * `tooltipValues` checks only apply when we know the slot vocabulary).
 */
export function findTemplateByHtmlUrl(htmlUrl, registry = HTML_TEMPLATE_REGISTRY) {
    return registry.find((t) => t.htmlUrl === htmlUrl);
}
/**
 * The sentinel a draft stores in a quiz question's `htmlUrl` to reference an
 * Admin-authored server-side template (the `html_template` table) — the
 * template analogue of `asset://<id>` for uploaded media. The export loads
 * the org's template rows and enters them into the registry under this exact
 * string, so `findTemplateByHtmlUrl`'s string-equality lookup keeps working;
 * the packager then substitutes + bundles the rendered HTML per language and
 * rewrites `htmlUrl` to the in-ZIP path, so the sentinel never reaches
 * content.xml.
 */
export const TEMPLATE_REF_REGEX = /^template:\/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
/** Build the `template://<id>` sentinel for a stored template row id. */
export function templateRef(templateId) {
    return `template://${templateId}`;
}
/** Extract the template row id from a `template://<id>` sentinel, or null. */
export function templateRefId(htmlUrl) {
    const m = TEMPLATE_REF_REGEX.exec(htmlUrl);
    return m?.[1] ?? null;
}
/** Minimal builder — single-locale label, empty description placeholder. */
function slot(name, enLabel) {
    return {
        name,
        label: { en: enLabel },
        description: { en: "" },
    };
}
//# sourceMappingURL=templates.js.map